"use client";

import { RiAppleFill, RiCalendarLine, RiGovernmentLine, RiLiveLine, RiSoundcloudFill, RiTimeLine } from "@remixicon/react";
import {
	formatSetTimeWithPreferences,
	formatShortDateWithPreferences,
} from "@workspace/shared-utils";
import { Card, CardContent } from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo } from "react";
import { Facepile } from "@/components/shared/facepile";
import { FacepileContainer } from "@/components/shared/facepile-skeleton";
import { FestivalPoster } from "@/components/shared/photos/festival-poster";
import { SetInfoDisplay } from "@/components/shared/set-info-display";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { formatStageNameFromString } from "@/lib/business-logic/set-rendering";

interface ArtistSet {
	id: string;
	startTime?: string | null;
	endTime?: string | null;
	artist?: {
		id: string;
		name: string;
		slug: string;
		image_url?: string | null;
	} | null;
	festival: {
		id: string;
		name: string;
		slug: string;
		start_date: string;
		end_date: string;
		poster_url?: string | null;
	};
	stage?: {
		id: string;
		name: string;
	} | null;
	friends_set?: boolean;
	special_guests?: string | null;
	is_collaborative?: boolean;
	display_format?: "B2B" | "x" | "+" | "F2F" | "&" | "vs" | "B3B" | "with" | null;
	alias_name?: string | null;
	is_group_alias?: boolean;
	presents_name?: string | null;
	group_alias_name?: string | null;
	member_artists?: Array<{
		artist_id: string;
		display_order: number;
		artist_name: string;
		artist_image_url?: string | null;
	}>;
	set_artists?: Array<{
		artist_id: string;
		position: number;
		artist_name: string;
	}>;
	youtube_url?: string | null;
	soundcloud_url?: string | null;
	apple_music_url?: string | null;
}

// Helper to determine which recording icon to show based on available URLs
const getRecordingIcon = (set: ArtistSet): "live" | "soundcloud" | "apple" | null => {
	const hasYoutube = !!set.youtube_url;
	const hasSoundcloud = !!set.soundcloud_url;
	const hasAppleMusic = !!set.apple_music_url;

	const sourceCount = [hasYoutube, hasSoundcloud, hasAppleMusic].filter(Boolean).length;

	if (sourceCount > 1 || hasYoutube) {
		return "live";
	}
	if (hasSoundcloud) {
		return "soundcloud";
	}
	if (hasAppleMusic) {
		return "apple";
	}
	return null;
};

interface ArtistSetCardProps {
	set: ArtistSet;
	attendees?: Array<{
		id: string;
		username: string | null;
		display_name: string;
		manual_avatar_url?: string | null;
		google_avatar_url?: string | null;
		rating?: number;
		loved?: boolean;
		must_see?: boolean;
		want_to_see?: boolean;
		saw_live?: boolean;
		saw_stream?: boolean;
		content?: string;
	}>;
	isFacepileLoading?: boolean;
}

const ArtistSetCard = memo(function ArtistSetCard({
	set,
	attendees = [],
	isFacepileLoading = false,
}: ArtistSetCardProps) {

	// Get user preferences for date formatting
	const userPreferences = useUserPreferences();

	// Generate the href for the set detail page
	const href = `/festivals/${set.festival.slug}/${set.id}`;

	return (
		<Link href={href} className="block">
			<Card
				className={cn(
					"relative w-full cursor-pointer overflow-hidden rounded-xl transition-all duration-150",
					"hover:bg-muted/50 dark:hover:bg-muted/20",
					"active:scale-[0.99] active:bg-muted dark:active:bg-muted/40",
				)}
			>
			<CardContent className="p-3">
				<div className="flex gap-3">
					{/* Festival Poster */}
					<div className="flex-shrink-0">
						<FestivalPoster
							name={set.festival.name}
							posterUrl={set.festival.poster_url}
							size="sm"
							loading="lazy"
							rounded="lg"
						/>
					</div>

					{/* Set Content */}
					<div className="min-w-0 flex-1 flex flex-col justify-between">
						{/* Top section: Set info */}
						<div className="min-w-0 flex-1">
							<SetInfoDisplay
								set={{
									id: set.id,
									artist: set.artist ? {
										id: set.artist.id,
										name: set.artist.name,
										image_url: set.artist.image_url,
										spotify_genres: null,
										manual_genres: null,
										aliases: [],
									} : null,
									stage: set.stage || null,
									start_time: set.startTime || "",
									end_time: set.endTime || set.startTime || "",
									// Required fields for ProcessedSet
									day: "unknown",
									startHour: 0,
									durationHours: 1,
									time:
										set.startTime && set.endTime
											? formatSetTimeWithPreferences(
													set.startTime,
													set.endTime,
													userPreferences,
													false,
												)
											: "",
									is_surprise: undefined,
									placeholder_name: null,
									// Use actual data from the set
									alias_name: set.alias_name,
									is_group_alias: set.is_group_alias,
									group_alias_name: set.group_alias_name,
									member_artists: set.member_artists || [],
									presents_name: set.presents_name,
									friends_set: set.friends_set ?? undefined,
									is_collaborative: set.is_collaborative,
									display_format: set.display_format || undefined,
									special_guests: set.special_guests,
									// Pass through set_artists for B2B display
									set_artists: set.set_artists?.map((sa, index) => ({
										id: `${set.id}-${sa.artist_id}`,
										set_id: set.id,
										artist_id: sa.artist_id,
										position: sa.position ?? index,
										artist: {
											id: sa.artist_id,
											name: sa.artist_name,
											image_url: null,
											spotify_genres: null,
											manual_genres: null,
											aliases: [],
										},
									})) || [],
								}}
								titleElement="h3"
								context="card"
								showStage={false}
								showTime={false}
								showPoster={false}
								// Let SetInfoDisplay show the actual set name (with B2B, presents, etc.)
								// Custom subtitle to show festival and venue info
								renderSubtitle={() => {
									const timeFormatted = formatSetTimeWithPreferences(
										set.startTime || "",
										set.endTime || "",
										userPreferences,
										false,
									);
									const dateFormatted = set.startTime
										? formatShortDateWithPreferences(
												set.startTime,
												userPreferences,
											)
										: null;

									// Format member artists for group aliases
									const memberSubtitle = set.is_group_alias && set.member_artists && set.member_artists.length > 0
										? set.member_artists
												.sort((a, b) => a.display_order - b.display_order)
												.map(m => m.artist_name)
												.join(` ${set.display_format || "x"} `)
										: null;

									return (
										<div className="space-y-0.5 text-muted-foreground text-xs">
											{/* Member artists subtitle for group aliases */}
											{memberSubtitle && (
												<div className="font-medium text-sm text-muted-foreground/70">{memberSubtitle}</div>
											)}
											{/* Festival name as first line */}
											<div className="font-medium">{set.festival.name}</div>
											<div className="flex items-center gap-1">
												<RiGovernmentLine className="h-3 w-3 flex-shrink-0" />
												<span className="text-left">
													{set.stage
														? formatStageNameFromString(set.stage.name)
														: "Stage: TBA"}
												</span>
											</div>
											{dateFormatted && (
												<div className="flex items-center gap-1">
													<RiCalendarLine className="h-3 w-3 flex-shrink-0" />
													<span className="text-left">{dateFormatted}</span>
												</div>
											)}
											{/* Time row with facepile */}
											<div className="flex items-center justify-between gap-2">
												<div className="flex items-center gap-1">
													<RiTimeLine className="h-3 w-3 flex-shrink-0" />
													<span className="text-left">{timeFormatted || "Set Time: TBA"}</span>
													{(() => {
														const recordingIcon = getRecordingIcon(set);
														if (!recordingIcon) return null;
														if (recordingIcon === "live") return <RiLiveLine className="h-3 w-3 flex-shrink-0" />;
														if (recordingIcon === "soundcloud") return <RiSoundcloudFill className="h-3 w-3 flex-shrink-0" />;
														if (recordingIcon === "apple") return <RiAppleFill className="h-3 w-3 flex-shrink-0" />;
														return null;
													})()}
												</div>

												{/* Facepile in same row as time */}
												<FacepileContainer hasData={attendees.length > 0}>
													<Facepile
														interactions={attendees}
														set={{
															id: set.id,
															artist: set.artist ? {
																id: set.artist.id,
																name: set.artist.name,
																image_url: set.artist.image_url,
																spotify_genres: null,
																manual_genres: null,
																aliases: [],
															} : null,
															stage: set.stage || null,
															start_time: set.startTime || "",
															end_time: set.endTime || set.startTime || "",
															day: "unknown",
															startHour: 0,
															durationHours: 1,
															time:
																set.startTime && set.endTime
																	? formatSetTimeWithPreferences(
																			set.startTime,
																			set.endTime,
																			userPreferences,
																			false,
																		)
																	: "",
															is_surprise: undefined,
															placeholder_name: null,
															alias_name: set.alias_name,
															is_group_alias: set.is_group_alias,
															presents_name: set.presents_name,
															friends_set: set.friends_set ?? undefined,
															is_collaborative: set.is_collaborative,
															display_format: set.display_format || undefined,
															special_guests: set.special_guests,
															set_artists: [],
														}}
														festivalSlug={set.festival.slug}
														maxAvatars={4}
														size="sm"
														disableLinks={true}
													/>
												</FacepileContainer>
											</div>
										</div>
									);
								}}
							/>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
		</Link>
	);
});

ArtistSetCard.displayName = "ArtistSetCard";

export { ArtistSetCard };
